import * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// project imports
import { useAppDispatch, useAppSelector } from 'hooks/useAppStore';
import BasicTable, {
  getSortParam,
  SortType
} from 'components/tables/BasicTable';
import { setBalanceGroupsList } from 'features/eai/eaiSlice';
import useTenant from 'hooks/useTenant';
import { BalanceGroupInterface } from 'features/eai/eaiTypes';
import ContextActionGuard from 'utils/guards/ContextActionGuard';
import { fetchAndSetEaiContextActions } from 'features/eai/thunks';
import { selectAccountInfo } from 'features/account/accountSelectors';
import useConfig from 'hooks/useConfig';

// material-ui
import VisibilityTwoToneIcon from '@mui/icons-material/VisibilityTwoTone';

// types
import { Row, TableAction } from 'models/table';
import {
  AppModule,
  ContextActions,
  DEFAULT_ROWS_PER_PAGE,
  EAIEntityTypeCode,
  Routes,
  TableActionType,
  TableRowCellType
} from 'utils/constants';
import {
  EaiAccessPageCA,
  GetContextActionsRequestBody
} from 'models/contextActions';
import { usePageAccessGuard } from 'hooks/usePageAccessGuard';
import { setActiveItem } from 'features/app/appSlice';
import { BALANCE_GROUP_MENU_ID } from 'routes/menu-items/eai';
import { ArrangementOrder, HeadCell } from 'models';
import { getBalanceGroupsList } from 'features/eai/thunks/balanceGroupThunks';
import {
  selectBalanceGroupsList,
  selectBalanceGroupsListPage
} from 'features/eai/selectors/balanceGroupSelectors';
import { debounce } from 'lodash';
import { useLocalStorageFilter } from 'hooks/useLocalStorageFilter';
import { replaceDynamicPathParams } from 'routes';

// constants
const I18N_ENTITY_ROOT = 'module.eai.balanceGroups';
const I18N_COMPONENT_ROOT = `${I18N_ENTITY_ROOT}.balanceGroupList`;
const { CA_EAI_LIST_BALANCE_GROUP } = ContextActions.EAI;

type BalanceGroupsFilterType = {
  search: string;
  sort: SortType;
};

const BalanceGroupList = (): JSX.Element => {
  const { t } = useTranslation();
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { localStorageRowsPerPage, onChangeLocaleStorageRowsPerPage } =
    useConfig();

  const accountInfo = useAppSelector(selectAccountInfo);
  const balanceGroupList = useAppSelector(selectBalanceGroupsList);
  const balanceGroupListPage = useAppSelector(selectBalanceGroupsListPage);

  const { localStorageFilterLoaded, localStorageFilterValue, saveFilter } =
    useLocalStorageFilter<BalanceGroupsFilterType>('balanceGroupsFilter');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [sort, setSort] = React.useState<SortType>({
    attribute: 'name',
    order: 'asc'
  });
  const [areFiltersLoaded, setAreFiltersLoaded] = useState(false);

  useEffect(
    () => {
      dispatch(setActiveItem([BALANCE_GROUP_MENU_ID]));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const canAccessPage = usePageAccessGuard(
    EaiAccessPageCA.CA_EAI_ACCESS_BALANCING_GROUP_PAGE
  );

  useEffect(() => {
    if (localStorageFilterLoaded && !areFiltersLoaded) {
      const loadedSearch = localStorageFilterValue?.search || '';
      setSearch(loadedSearch);

      const loadedSort = localStorageFilterValue?.sort;
      if (loadedSort) {
        setSort(loadedSort);
      }

      const sortParam = getSortParam(loadedSort || sort);

      dispatch(
        getBalanceGroupsList(
          loadedSearch,
          localStorageRowsPerPage,
          0,
          sortParam
        )
      );
      setAreFiltersLoaded(true);
    }
  }, [
    localStorageFilterLoaded,
    localStorageRowsPerPage,
    localStorageFilterValue,
    areFiltersLoaded,
    dispatch,
    sort
  ]);

  useEffect(() => {
    return () => {
      dispatch(setBalanceGroupsList([]));
    };
  }, [dispatch]);

  useEffect(() => {
    if (!canAccessPage) {
      return;
    }

    if (accountInfo?.sub) {
      const requestBody: GetContextActionsRequestBody = [
        {
          resourceEntity: EAIEntityTypeCode.USER,
          identificators: [accountInfo.sub],
          contextActions: [CA_EAI_LIST_BALANCE_GROUP]
        }
      ];

      dispatch(fetchAndSetEaiContextActions(requestBody));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccessPage, accountInfo]);

  const debouncedSearch = React.useRef(
    debounce((searchString: string) => {
      const sortParam = getSortParam(sort);

      dispatch(
        getBalanceGroupsList(
          searchString,
          localStorageRowsPerPage,
          0,
          sortParam
        )
      );
    }, 500)
  ).current;

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(0);
    saveFilter({ search: value, sort: sort });
    debouncedSearch.cancel();
    debouncedSearch(value);
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    setPage(newPage);

    const sortParam = getSortParam(sort);

    dispatch(
      getBalanceGroupsList(search, localStorageRowsPerPage, newPage, sortParam)
    );
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | undefined
  ) => {
    const newSize = event?.target.value
      ? parseInt(event.target.value, 10)
      : DEFAULT_ROWS_PER_PAGE;
    onChangeLocaleStorageRowsPerPage(newSize);
    setPage(0);

    const sortParam = getSortParam(sort);

    dispatch(getBalanceGroupsList(search, newSize, 0, sortParam));
  };

  const handleSortRequest = (
    _event: React.SyntheticEvent,
    property: string
  ) => {
    let newOrder: NonNullable<ArrangementOrder> = 'asc';

    const currentSortAttribute = sort.attribute;
    if (property === currentSortAttribute) {
      newOrder = sort.order === 'asc' ? 'desc' : 'asc';
    }

    const newSort = { attribute: property, order: newOrder };

    setSort(newSort);
    const sortParam = getSortParam(newSort);

    saveFilter({ search: search, sort: newSort });

    dispatch(
      getBalanceGroupsList(search, localStorageRowsPerPage, page, sortParam)
    );
  };

  const generateUserActions = (
    balanceGroup: BalanceGroupInterface
  ): TableAction[] => {
    return [
      {
        type: TableActionType.DETAIL,
        icon: <VisibilityTwoToneIcon />,
        link: Routes.EAI.BALANCE_GROUP_DETAIL_PATH,
        title: t(`common.table.detail`) || '',
        onClick: () => {
          navigate(
            replaceDynamicPathParams(Routes.EAI.BALANCE_GROUP_DETAIL_PATH, [
              {
                paramName: 'tenant',
                value: tenant
              },
              {
                paramName: 'balanceGroupId',
                value: balanceGroup.id
              }
            ])
          );
        }
      }
    ];
  };

  const headCells: HeadCell[] = [
    {
      id: 'name',
      numeric: false,
      disablePadding: false,
      label: t(`${I18N_COMPONENT_ROOT}.columns.name`),
      align: 'left',
      sortable: true
    },
    {
      id: 'eic',
      numeric: false,
      disablePadding: false,
      label: t(`${I18N_COMPONENT_ROOT}.columns.eic`),
      align: 'right',
      sortable: false
    },
    {
      id: 'emsPartyName',
      numeric: false,
      disablePadding: false,
      label: t(`${I18N_COMPONENT_ROOT}.columns.emsPartyName`),
      align: 'right',
      sortable: true
    },
    {
      id: 'actions',
      numeric: false,
      disablePadding: false,
      label: t(`common.table.actions`),
      align: 'right',
      sortable: false
    }
  ];

  const rows: Row[] = balanceGroupList.map((balanceGroup) => {
    return {
      id: balanceGroup.id.toString(),
      cells: [
        {
          name: 'name',
          value: balanceGroup.name,
          type: TableRowCellType.STRING,
          align: 'left'
        },
        {
          name: 'eic',
          value: balanceGroup.eic,
          type: TableRowCellType.STRING,
          align: 'right'
        },
        {
          name: 'emsPartyName',
          value: balanceGroup.emsPartyName,
          type: TableRowCellType.STRING,
          align: 'right'
        },
        {
          name: 'actions',
          value: generateUserActions(balanceGroup),
          type: TableRowCellType.ACTIONS,
          align: 'right'
        }
      ]
    };
  });

  return canAccessPage ? (
    <ContextActionGuard
      module={AppModule.EAI}
      actionCode={CA_EAI_LIST_BALANCE_GROUP}
    >
      <BasicTable
        header={headCells}
        rows={rows}
        pagination={balanceGroupListPage}
        batchProcessing={false}
        page={page}
        search={search}
        rowsPerPage={localStorageRowsPerPage}
        onSearchChange={handleSearchChange}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        onSortRequest={handleSortRequest}
        order={sort.order}
        orderBy={sort.attribute}
        i18nComponentRoot={I18N_COMPONENT_ROOT}
        appModule={AppModule.EAI}
      />
    </ContextActionGuard>
  ) : (
    <></>
  );
};

export default BalanceGroupList;
